import React, { useMemo, useState } from 'react';

import { userApis } from '@apis/next/userApis';
import registrationImage from '@assets/img/arts/registration.png';
import d2dLogo from '@assets/img/icons/d2d-white.png';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import { Layout } from '@components/Layout';
import { CheckboxBooleanInput } from '@components/NextForms/CheckboxBooleanInput';
import { ErrorMessages } from '@components/NextForms/ErrorMessages';
import { TextInput } from '@components/NextForms/TextInput';
import { useFormUtils } from '@hooks/useFormUtils';
import { RegistrationForm } from '@models/next/User';
import type { MobxZodField, MobxZodTypes } from '@monoid-dev/mobx-zod-form';
import { getForm, useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles, useIsMobile } from '@styles';
import { createStrings } from '@utils/local';
import { f } from '@utils/nextForms';
import { url } from '@utils/pages';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: '【D2D】無料アカウント作成｜中国貿易に特化した海上輸送システム',
    formTitle: '無料アカウントの作成',
    introduction: () => (
      <>
        ようこそ、新しい物流のフィールドへ！
        <br />
        D2Dはあなたの輸送をイノベーションするためのたくさんの機能が詰まった次世代の輸送サービスです。
        <br />
        <br />
        まずは無料アカウントを作成してさっそくD2Dの機能に触れてみましょう！
        <br />
      </>
    ),
    continue: '認証へ進む',
    passwordHelper: '（パスワードは半角英数字8～16文字で入力してください）',
    privacyPolicyLabel: ({ privatePolicy, privatePolicyLink }: any) => (
      <span className={privatePolicy}>
        <a href="/about/PrivacyPolicy" target="_blank" className={privatePolicyLink}>
          プライバシーポリシー
        </a>
        に同意する
      </span>
    ),
    privacyClaim:
      'お客様がFCSLのサービスをご利用になる際、FCSLは、お客様の情報を託していただくことになります。FCSLはこのことに伴う重大な責任を認識し、お客様の情報を保護し、お客様がご自身の情報を管理できるようにすべく尽力しています。',
    verifyEmailPrompt: (email: string) => (
      <>
        <span role="title">ご登録はまだ完了していません。</span>
        <br />
        <br />
        以下のメールアドレスにご本人確認用のメールを送信しました：
        <br />
        <br />
        <span>{email}</span>
        <br />
        <br />
        メールが届かない場合、メールアドレスに間違いがあるか、
        <br />
        もしくは登録手続きが正しく完了していない可能性があります。
        <br />
        その場合はお手数ですが、D2D MEMBERSの登録を再度やり直してくださいますようお願い致します。
        <br />
      </>
    ),
    phonePlaceholder: '半角数字で入力してください',
    lastNamePlaceholder: '運輸',
    firstNamePlaceholder: '太郎',
    companyPlaceholder: '株式会社ABC',
    emailPlaceholder: 'abc@d2dasia.com',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumnCenter,
    flex: 1,
    backgroundColor: theme.palette.base[200],
    paddingTop: 50,
    paddingBottom: 50,
    [theme.breakpoints.down('sm')]: {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  cardContainer: {
    width: 1000,
    height: 600,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    overflow: 'hidden',
    borderRadius: 2,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      gridTemplateColumns: '1fr',
      height: 'auto',
    },
  },
  imageContainer: {
    backgroundImage: `url(${JSON.stringify(registrationImage)})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    paddingLeft: 44,
    paddingTop: 25,
    paddingRight: 44,
  },
  logo: {
    width: 172,
    height: 40,
    marginBottom: '2rem',
    [theme.breakpoints.down('sm')]: {
      width: 86,
      height: 20,
    },
  },
  formContainer: {
    ...common.flexColumn,
    ...common.middleText,
    padding: '25px 44px',
    rowGap: '0.4rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px',
      ...common.fontWeight700,
      rowGap: '0.625rem',
      padding: '25px 20px',
    },
  },
  formTitle: {
    ...common.biggerText,
    padding: '4px 0px',
  },
  textInputContainer: {
    ...common.flexColumn,
    ...common.textGray500,
    minWidth: '100px',
    [theme.breakpoints.down('sm')]: {
      color: theme.palette.grey[700],
    },
  },
  textInputInput: {
    height: 32,
    paddingLeft: '8px !important',
    paddingRight: '8px !important',
    border: `1px solid ${theme.palette.grey[500]}`,
    backgroundColor: 'transparent',
    marginTop: '8px !important',
    outline: 'none',
    '&:focus': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    '$error &': {
      border: `1px solid ${theme.palette.error.main}`,
    },
    [theme.breakpoints.down('sm')]: {
      height: 40,
      fontSize: '0.75rem',
      ...common.fontWeight700,
    },
  },
  textInputHint: {
    [theme.breakpoints.down('sm')]: {
      color: theme.palette.grey[500],
    },
  },
  introduction: {
    ...common.textBase100,
    ...common.fontWeight600,
    fontSize: 20,
    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
      padding: '0px 0px 20px 0px',
    },
  },
  nameContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 40px 1fr',
  },
  nameDash: {
    marginLeft: 16,
    marginRight: 16,
    marginBottom: 16,
    height: 1,
    backgroundColor: theme.palette.grey[500],
    alignSelf: 'end',
    [theme.breakpoints.down('sm')]: {
      marginBottom: 20,
    },
  },
  privatePolicy: {
    ...common.middleText,
    color: theme.palette.grey['500'],
    marginLeft: '0.5rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px',
      ...common.fontWeight700,
      color: theme.palette.grey[700],
    },
  },
  privatePolicyLink: {
    color: theme.palette.primary.main,
  },
  privacyClaim: {
    ...common.smallerText,
    ...common.textGray500,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
    },
  },
  submitButton: {
    [theme.breakpoints.down('sm')]: {
      ...common.middleText,
    },
  },
  error: {},
  paper: {
    textAlign: 'center',
    whiteSpace: 'pre',
  },
  verifyEmailPrompt: {
    ...common.middleText,
    ...common.fontWeight400,
    '& span': {
      ...common.normalText,
      ...common.fontWeight700,
    },
    '& span[role="title"]': {
      color: theme.palette.primary.main,
      ...common.normalText,
      ...common.fontWeight700,
    },
  },
}));

interface CreateAccoutTextInputProps {
  field: MobxZodField<MobxZodTypes>;
  hint?: React.ReactNode;
  placeholder?: string;
}

const CreateAccountTextInput: React.FC<CreateAccoutTextInputProps> = (props) => {
  const { field, hint, placeholder } = props;

  const form = useMemo(() => getForm(field), [field]);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={clsx(classes.textInputContainer)}>
      <div style={{ marginBottom: 4 }}>
        <label {...form.bindLabel(field)} />
        {hint}
        {/* In case hint is too big */}
        {!hint && (
          <ErrorMessages
            field={field}
            container="span"
            containerProps={{ style: { marginLeft: '0.6rem' } }}
            delimiter=""
          />
        )}
      </div>

      <TextInput
        field={field}
        withErrorMessages={false}
        inputProps={{
          placeholder,
          autoComplete: f.isSecret(field.type) ? 'new-password' : undefined,
        }}
      />
    </div>
  );
};

export const CreateAccount: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const history = useHistory();
  const [dialogOpen, setDialogOpen] = useState(false);

  const registration = userApis.usePost('/registration');

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [registration] });

  const [verifyEmail, setVerifyEmail] = useState('');
  const isMobile = useIsMobile();

  const form = useForm(RegistrationForm);

  const {
    root: { fields },
  } = form;

  const navigateToLogin = () =>
    history.push(
      `${url('/auth/Login')}?emailVerified=false&email=${encodeURIComponent(verifyEmail)}`,
    );

  const renderModal = () => (
    <Dialog
      open={dialogOpen}
      onClose={navigateToLogin}
      classes={{
        paper: classes.paper,
      }}
      muiDialogProps={{
        PaperProps: {
          onClick: navigateToLogin,
        },
      }}
    >
      <div className={classes.verifyEmailPrompt}>{strings.verifyEmailPrompt(verifyEmail)}</div>
    </Dialog>
  );

  return (
    <Layout title={strings.title}>
      <div className={classes.container}>
        <div>
          {renderAlertsAndSpinnerModal()}

          {renderModal()}

          <form
            className={classes.cardContainer}
            {...form.bindForm({
              async onSubmit(values) {
                await registration.mutateAsync(values);
                setVerifyEmail(values.email);

                if (isMobile) {
                  history.push(
                    `${url('/auth/CreateAccountVerifyEmail')}?email=${encodeURIComponent(
                      values.email,
                    )}`,
                  );
                } else {
                  setDialogOpen(true);
                }
              },
            })}
          >
            {!isMobile && (
              <div className={classes.imageContainer}>
                <img src={d2dLogo} alt="D2D Logo" className={classes.logo} />
                <div className={classes.introduction}>{strings.introduction()}</div>
              </div>
            )}
            <div className={classes.formContainer}>
              {isMobile && <div className={classes.formTitle}>{strings.formTitle}</div>}

              <CreateAccountTextInput
                field={fields.company_name}
                placeholder={strings.companyPlaceholder}
              />

              <div className={classes.nameContainer}>
                <CreateAccountTextInput
                  field={fields.last_name}
                  placeholder={strings.lastNamePlaceholder}
                />
                <div className={classes.nameDash} />
                <CreateAccountTextInput
                  field={fields.first_name}
                  placeholder={strings.firstNamePlaceholder}
                />
              </div>

              <CreateAccountTextInput
                field={fields.cellphone}
                placeholder={strings.phonePlaceholder}
              />

              <CreateAccountTextInput field={fields.email} placeholder={strings.emailPlaceholder} />

              <CreateAccountTextInput
                field={fields.password1}
                hint={<span className={classes.smallerText}>{strings.passwordHelper}</span>}
              />

              <CreateAccountTextInput field={fields.password2} />

              <div>
                <CheckboxBooleanInput field={fields.agree_to_privacy_policy} />

                {strings.privacyPolicyLabel(classes)}
              </div>

              <div className={clsx(classes.privacyClaim)}>{strings.privacyClaim}</div>

              <Button
                className={classes.submitButton}
                color="primary"
                text={strings.continue}
                type="submit"
              />
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};
